import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Instagram } from 'lucide-react';
import { siteInfo, footerLinks } from '../data/mockData';

const Footer = () => {
  const servicosLinks = [
    { name: 'Funeral Completo', path: '/funeral' },
    { name: 'Cremação', path: '/cremacao' },
    { name: 'Translado', path: '/translado' },
    { name: 'Valores de Funeral', path: '/valor-funeral-rj' }
  ];

  const regioesLinks = [
    { name: 'Funerária no Meier', path: '/funeraria-no-meier' },
    { name: 'Funerária na Tijuca', path: '/funeraria-na-tijuca' },
    { name: 'Funerária no Centro', path: '/funeraria-no-centro' },
    { name: 'Funerária Barra da Tijuca', path: '/funeraria-barra-da-tijuca' },
    { name: 'Funerária em Botafogo', path: '/funeraria-em-botafogo' },
    { name: 'Zona Norte e Zona Sul', path: '/funeraria-zona-norte-zona-sul' }
  ];

  const institucionalLinks = [
    { name: 'Início', path: '/' },
    { name: 'Quem Somos', path: '/quem-somos' },
    { name: 'Contato', path: '/contato' }
  ];

  return (
    <footer className="bg-black text-white pt-16 pb-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8 mb-12">
          {/* Logo e descrição */}
          <div className="col-span-1">
            <Link to="/" className="flex items-center space-x-3 mb-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_funeraria-meier-rj/artifacts/8dv5zw1l_logp.jfif" 
                alt="Funerária do Meier Logo"
                className="w-16 h-16 rounded-full object-cover"
              />
            </Link>
            <div className="text-white mb-2">
              <div className="text-xl font-serif">{siteInfo.name.split(' ')[0]} {siteInfo.name.split(' ')[1]}</div>
              <div className="text-xs tracking-wider text-gray-400">{siteInfo.tagline}</div>
            </div>
          </div>

          {/* Serviços */}
          <div>
            <h3 className="text-lg font-semibold mb-4 text-teal-400">Serviços</h3>
            <ul className="space-y-2">
              {servicosLinks.map((link, index) => (
                <li key={index}>
                  <Link
                    to={link.path}
                    className="text-gray-400 hover:text-teal-400 transition-colors text-sm block"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Regiões Atendidas */}
          <div>
            <h3 className="text-lg font-semibold mb-4 text-teal-400">Regiões Atendidas</h3>
            <ul className="space-y-2">
              {regioesLinks.map((link, index) => (
                <li key={index}>
                  <Link
                    to={link.path}
                    className="text-gray-400 hover:text-teal-400 transition-colors text-sm block"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Institucional */}
          <div>
            <h3 className="text-lg font-semibold mb-4 text-teal-400">Institucional</h3>
            <ul className="space-y-2">
              {institucionalLinks.map((link, index) => (
                <li key={index}>
                  <Link
                    to={link.path}
                    className="text-gray-400 hover:text-teal-400 transition-colors text-sm block"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contatos */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Contatos</h3>
            <ul className="space-y-3">
              <li className="flex items-start space-x-2">
                <Phone className="w-4 h-4 text-teal-400 mt-1 flex-shrink-0" />
                <a href={`tel:${siteInfo.phone.replace(/\D/g, '')}`} className="text-gray-400 hover:text-teal-400 transition-colors text-sm">
                  {siteInfo.phone}
                </a>
              </li>
              <li className="flex items-start space-x-2">
                <Mail className="w-4 h-4 text-teal-400 mt-1 flex-shrink-0" />
                <a href={`mailto:${siteInfo.email}`} className="text-gray-400 hover:text-teal-400 transition-colors text-sm break-all">
                  {siteInfo.email}
                </a>
              </li>
              <li className="flex items-start space-x-2">
                <MapPin className="w-4 h-4 text-teal-400 mt-1 flex-shrink-0" />
                <span className="text-gray-400 text-sm">{siteInfo.location}</span>
              </li>
              <li className="flex items-start space-x-2">
                <Instagram className="w-4 h-4 text-teal-400 mt-1 flex-shrink-0" />
                <a 
                  href="https://www.instagram.com/santacasa.assistencial?igsh=dHExcWppNXhldDNk" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-gray-400 hover:text-teal-400 transition-colors text-sm"
                >
                  @santacasa.assistencial
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Redes Sociais */}
        <div className="border-t border-gray-800 pt-8 pb-8">
          <div className="flex justify-center items-center space-x-6">
            <a
              href="https://wa.me/552141064022"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-green-600 hover:bg-green-700 text-white p-3 rounded-full transition-all duration-300 hover:scale-110"
              aria-label="WhatsApp"
            >
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
              </svg>
            </a>
            
            <a
              href="https://www.instagram.com/santacasa.assistencial?igsh=dHExcWppNXhldDNk"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-gradient-to-br from-purple-600 via-pink-600 to-orange-500 hover:from-purple-700 hover:via-pink-700 hover:to-orange-600 text-white p-3 rounded-full transition-all duration-300 hover:scale-110"
              aria-label="Instagram"
            >
              <Instagram className="w-6 h-6" />
            </a>

            <a
              href={`tel:${siteInfo.phone.replace(/\D/g, '')}`}
              className="bg-teal-600 hover:bg-teal-700 text-white p-3 rounded-full transition-all duration-300 hover:scale-110"
              aria-label="Telefone"
            >
              <Phone className="w-6 h-6" />
            </a>
          </div>
        </div>

        {/* Copyright */}
        <div className="border-t border-gray-800 pt-8 text-center">
          <p className="text-gray-500 text-sm">
            Copyright © 2026 {siteInfo.name.toUpperCase()} | Serviço Funerário Imediato 24h - Funerária Completa no Meier, Tijuca, Centro, Barra e Botafogo - RJ | Cremação | Translado | Velório | Sepultamento | Atendimento Funerário Zona Norte e Zona Sul
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
